import type { Conversation, Message } from '../../types/Inbox';
import { callAdminApi } from './wixAdminApi';
import { ConversationCacheService } from './conversationCacheService';
import { FirestoreCacheService } from './firestoreCacheService';
import { isSyncedExternalMessage } from '../../utils/syncedMessages';

/**
 * Inbox Service - Frontend API service
 * NOW USES FIRESTORE CACHE FIRST with fallback to direct Wix API
 *
 * Architecture:
 * 1. PRIMARY: Firestore cache (fast, cost-effective)
 * 2. FALLBACK: Direct Wix API via callAdminApi (slow, expensive)
 */
export class InboxService {
  /**
   * Get all conversations for the current user
   *
   * CACHE-FIRST STRATEGY:
   * 1. Try Firestore cache (99% cost reduction, ~100ms)
   * 2. Fallback to direct Wix API if cache fails (slow, expensive)
   */
  static async getConversations(
    onProgressUpdate?: (conversations: Conversation[]) => void
  ): Promise<Conversation[]> {
    const startTime = Date.now();

    try {
      console.log(`📭 InboxService.getConversations: Attempting Firestore cache first...`);

      // PRIMARY: Try Firestore cache
      const cacheService = ConversationCacheService.getInstance();
      const cachedConversations = await cacheService.getCachedConversations();

      if (cachedConversations && cachedConversations.length > 0) {
        const cacheTime = Date.now() - startTime;
        console.log(`✅ Loaded ${cachedConversations.length} conversations from Firestore cache in ${cacheTime}ms`);

        if (typeof onProgressUpdate === 'function') {
          onProgressUpdate(cachedConversations);
        }

        return cachedConversations;
      }

      // FALLBACK: Cache empty or failed, use direct Wix API
      console.warn(`⚠️ Firestore cache empty or unavailable, falling back to direct Wix API (slow)`);
      return await this.getConversationsFromWixAPI(onProgressUpdate);

    } catch (error) {
      console.error(`❌ Error in getConversations (cache attempt):`, error);

      // FALLBACK: On error, use direct Wix API
      console.warn(`⚠️ Firestore cache failed, falling back to direct Wix API`);
      return await this.getConversationsFromWixAPI(onProgressUpdate);
    }
  }

  /**
   * FALLBACK METHOD: Get conversations directly from Wix API
   * Only used when Firestore cache is empty or fails
   *
   * This is the OLD implementation - loops through all contacts
   */
  private static async getConversationsFromWixAPI(
    onProgressUpdate?: (conversations: Conversation[]) => void
  ): Promise<Conversation[]> {
    const startTime = Date.now();
    try {
      console.log(`⏱️ START: Fetching all conversations from Wix API (FALLBACK)...`);

      // Get ALL contacts with specific field projections to ensure we get name data
      console.log(`📋 Querying contacts from Wix...`);
      const queryStart = Date.now();
      
      // Fetch ALL contacts using CURSOR-BASED pagination
      let allContacts: Array<{
        _id: string;
        info?: { name?: { first?: string; last?: string } };
        primaryInfo?: { email?: string };
        picture?: string | { url?: string }; // Can be a direct URL string or object with url
        avatar?: string;
        displayName?: string;
      }> = [];
      let cursor: string | undefined;
      let hasMoreContacts = true;
      let pageCount = 0;

      while (hasMoreContacts) {
        pageCount++;
        const contactsResult = await callAdminApi('queryContacts', 'contacts', {
          limit: 500,
          ...(cursor && { cursor }),
          fields: ['_id', 'info.name.first', 'info.name.last', 'primaryInfo.email', 'picture.url', 'avatar', 'displayName'],
        }) as {
          _items?: Array<{
            _id: string;
            info?: { name?: { first?: string; last?: string } };
            primaryInfo?: { email?: string };
            picture?: string | { url?: string }; // Can be a direct URL string or object with url
            avatar?: string;
            displayName?: string;
          }>;
          items?: Array<{
            _id: string;
            info?: { name?: { first?: string; last?: string } };
            primaryInfo?: { email?: string };
            picture?: string | { url?: string }; // Can be a direct URL string or object with url
            avatar?: string;
            displayName?: string;
          }>;
          pagingMetadata?: { cursors?: { next?: string } };
          length?: number;
        };

        const pageContacts = contactsResult?._items || contactsResult?.items || [];
        if (pageContacts.length > 0) {
          allContacts = allContacts.concat(pageContacts);
          console.log(`   📋 Page ${pageCount}: fetched ${pageContacts.length} contacts (total so far: ${allContacts.length})`);
        }

        // Get next cursor for pagination
        cursor = (contactsResult?.pagingMetadata as { cursors?: { next?: string } } | undefined)?.cursors?.next;
        hasMoreContacts = !!cursor;
      }

      const queryTime = Date.now() - queryStart;
      console.log(`⏱️ Contact query took: ${queryTime}ms (${allContacts.length} contacts across ${pageCount} pages)`);

      if (!allContacts || allContacts.length === 0) {
        return [];
      }

      console.log(`⚡ Processing ${allContacts.length} contacts in FULL PARALLEL...`);

      // Process ALL contacts in PARALLEL (max concurrency, no batching, no delays)
      const conversationPromises = allContacts.map(async (contact, index) => {
        try {
          // Log first few contacts to debug structure
          if (index < 3) {
            console.log(`📭 Contact ${index} structure:`, {
              _id: contact._id,
              info: contact.info,
              primaryInfo: contact.primaryInfo,
              picture: contact.picture,
              displayName: contact.displayName
            });
          }

          // Try to get existing conversation WITHOUT creating a new one
          // First, try to fetch messages using a placeholder conversation ID
          // If that fails, THEN create the conversation
          let conversationId: string | undefined;
          let conversation: Record<string, unknown> | undefined;

          // For existing conversations, we need the ID. Try getMessages with different approaches
          // Wix may allow querying messages by participant ID without needing conversation ID
          // But since it requires conversationId, we need to call startConversation to get the ID
          // NOTE: startConversation creates if not exists, gets if exists - both work for us
          const conversationResponse = await callAdminApi('startConversation', 'inbox', {
            participantIds: [contact._id],
          }) as {
            conversation?: {
              id?: string;
              _id?: string;
              channelType?: string;
              participants?: Array<Record<string, unknown>>;
            };
            id?: string;
            _id?: string;
            channelType?: string;
            participants?: Array<Record<string, unknown>>;
          };

          conversation = conversationResponse?.conversation || conversationResponse;
          conversationId = (conversation?.id || conversation?._id) as string | undefined;

          // Log conversation response to see if it has participant info
          if (index < 3) {
            console.log(`📭 startConversation response for contact ${contact._id}:`, conversation);
          }

          if (!conversationId) return null;

          // Extract contact name - try multiple locations
          const firstName = contact.info?.name?.first || '';
          const lastName = contact.info?.name?.last || '';
          const email = contact.primaryInfo?.email || '';
          const displayName = contact.displayName || '';

          // Build name with proper fallback: displayName > first+last > email > 'Unknown'
          let name = displayName || (firstName + ' ' + lastName).trim();
          if (!name && email) {
            name = email;
          } else if (!name) {
            name = 'Unknown';
          }

          // For email-only contacts (no first/last name), always prefer email as display name
          if (!firstName && !lastName && !displayName && email) {
            name = email;
          }

          if (index < 3) {
            console.log(`📭 Conversation for contact ${contact._id}: name="${name}" (displayName="${displayName}" first="${firstName}" last="${lastName}" email="${email}")`);
          }

          // Fetch ALL messages from conversation using cursor-based pagination
          try {
            let allMessages: Array<{
              content?: {
                contentType?: string;
                text?: string;
                previewText?: string
              };
              _createdDate?: string;
              sender?: { name?: string; contactId?: string };
              visibility?: string;
              direction?: string;
            }> = [];
            let cursor: string | undefined;
            let hasMore = true;
            let pageCount = 0;

            while (hasMore) {
              pageCount++;
              const paging: Record<string, number | string> = { limit: 100 };
              if (cursor) {
                paging.cursor = cursor;
              }

              // Fetch ALL messages including those with visibility="BUSINESS"
              // This ensures we get form submissions which may have visibility="BUSINESS"
              // Our filtering logic below will exclude automated business notifications
              const messagesResult = await callAdminApi('getMessages', 'inbox', {
                conversationId,
                visibility: 'ALL',
                options: {
                  paging
                }
              }) as Record<string, unknown>;

              if (index < 3 && pageCount === 1) {
                console.log(`📨 Full messagesResult for first page:`, JSON.stringify(messagesResult, null, 2).substring(0, 500));
              }

              // Handle different response structures from Wix API
              const pageMessages = (messagesResult?.messages as Array<{
                _id?: string;
                content?: {
                  contentType?: string;
                  text?: string;
                  previewText?: string
                };
                _createdDate?: string;
                sender?: { name?: string; contactId?: string };
                visibility?: string;
                direction?: string;
              }>) || [];
              
              // Check for pagination metadata in different locations
              const pagingMeta = (messagesResult?.pagingMetadata || 
                                messagesResult?.paging ||
                                messagesResult?.pagination || {}) as { cursors?: { next?: string }; next?: string; tooManyToCount?: boolean };
              
              // Extract next cursor from different possible locations
              let nextCursor = pagingMeta?.cursors?.next || 
                             pagingMeta?.next || 
                             (messagesResult?.nextCursor as string | undefined);
              
              // If there's a tooManyToCount flag, it means there are more messages
              if (pagingMeta?.tooManyToCount && !nextCursor) {
                // Use the last message as a cursor placeholder
                if (pageMessages.length > 0) {
                  nextCursor = pageMessages[pageMessages.length - 1]?._id;
                }
              }

              if (index < 3) {
                console.log(`📨 Conversation ${contact._id}: Page ${pageCount} fetched ${pageMessages.length} messages`);
              }

              if (pageMessages.length === 0) {
                hasMore = false;
                break;
              }

              allMessages = allMessages.concat(pageMessages as Array<{
                content?: {
                  contentType?: string;
                  text?: string;
                  previewText?: string
                };
                _createdDate?: string;
                sender?: { name?: string; contactId?: string };
                visibility?: string;
                direction?: string;
              }>);
              
              // Update cursor for next iteration
              cursor = nextCursor;
              if (index < 3) {
                console.log(`📨 Conversation ${contact._id}: Page ${pageCount} - Next cursor: ${cursor ? 'YES' : 'NO'}`);
              }
              hasMore = !!cursor;
            }

            if (index < 3) {
              console.log(`📨 Conversation ${contact._id}: Total ${allMessages.length} messages across ${pageCount} pages`);
              if (allMessages.length > 0) {
                const dates = allMessages.map((m: { _createdDate?: string }) => m._createdDate).filter((d: string | undefined) => d);
                console.log(`   Date range: ${dates[0]} to ${dates[dates.length - 1]}`);
              }
            }

            // CRITICAL: Skip conversations where ALL messages are automated business notifications
            // INCLUDE: Form submissions (even if visibility="BUSINESS")
            // EXCLUDE: Email campaigns, invoices, etc. with visibility="BUSINESS"
            const allMessagesAreBusinessNotifications = allMessages.every((msg) => {
              const visibility = msg.visibility;
              const contentType = msg.content?.contentType;

              // Form submissions are real participant interactions, even if visibility="BUSINESS"
              if (contentType === 'FORM') {
                return false; // NOT a business notification
              }

              // So are messages synced in from an outside service (email), which Wix's sync flow also marks BUSINESS
              if (isSyncedExternalMessage(msg)) {
                return false;
              }

              // Everything else with visibility="BUSINESS" is a business notification
              return visibility === 'BUSINESS';
            });

            if (allMessagesAreBusinessNotifications) {
              if (index < 3) {
                console.log(`⏭️  SKIPPING: ${name} - ALL messages are automated business notifications`);
              }
              return null;
            }

            // Find messages for lastMessage display
            const nonBusinessMessages = allMessages.filter((msg) => {
              const visibility = msg.visibility;
              const contentType = msg.content?.contentType;

              // Include non-BUSINESS messages
              if (visibility !== 'BUSINESS') {
                return true;
              }

              // Include FORM submissions even if visibility="BUSINESS"
              if (contentType === 'FORM') {
                return true;
              }

              // Include synced outside-service messages (email), which are also marked BUSINESS
              if (isSyncedExternalMessage(msg)) {
                return true;
              }

              return false;
            });

            if (nonBusinessMessages.length === 0) {
              if (index < 3) {
                console.log(`⏭️  SKIPPING: ${name} - no non-business messages found`);
              }
              return null;
            }

            // Try to get contact name from ANY message sender with contactId
            if (!name || name.startsWith('Contact ')) {
              const contactMessage = allMessages.find(m => m.sender?.contactId && m.sender?.name);
              if (contactMessage?.sender?.name) {
                name = contactMessage.sender.name;
                if (index < 3) {
                  console.log(`📭 Got name from message sender: "${name}"`);
                }
              }
            }

            // For lastMessage display AND timestamp: use the most recent NON-BUSINESS message
            // Sort nonBusinessMessages by timestamp to get the most recent
            const sortedNonBusinessMessages = [...nonBusinessMessages].sort((a, b) => {
              const dateA = a._createdDate ? new Date(a._createdDate).getTime() : 0;
              const dateB = b._createdDate ? new Date(b._createdDate).getTime() : 0;
              return dateB - dateA; // Descending order (newest first)
            });

            const lastNonBusinessMsg = sortedNonBusinessMessages[0];

            // Use the last non-business message for preview
            const messageText = lastNonBusinessMsg?.content?.text || lastNonBusinessMsg?.content?.previewText || '';
            let lastMessageDate = new Date(0);
            if (lastNonBusinessMsg?._createdDate) {
              const createdDate = lastNonBusinessMsg._createdDate;
              if (typeof createdDate === 'string') {
                lastMessageDate = new Date(createdDate);
              } else if (typeof createdDate === 'object' && createdDate !== null && 'getTime' in (createdDate as Record<string, unknown>)) {
                lastMessageDate = createdDate as Date;
              } else if (typeof createdDate === 'number') {
                lastMessageDate = new Date(createdDate);
              }
            }

            // Get avatar URL from contact - check both picture and avatar fields
            // Wix returns picture as either a string URL or an object {url: string}
            // Some contacts have picture, others have avatar field
            let avatarUrl: string | undefined;
            if (typeof contact.picture === 'string') {
              avatarUrl = contact.picture;
            } else if (contact.picture && typeof contact.picture === 'object' && 'url' in contact.picture) {
              avatarUrl = (contact.picture as { url?: string }).url;
            } else {
              avatarUrl = contact.avatar;
            }

            // If no avatar, set to undefined to show initials
            if (!avatarUrl) {
              avatarUrl = undefined;
            }

            return {
              id: conversationId,
              name: name,
              lastMessage: messageText.substring(0, 100),
              timestamp: lastMessageDate,
              messages: [],
              avatarUrl: avatarUrl,
              unread: false,
              unreadCount: 0,
              starred: false,
              archived: false,
              priority: 'medium',
              channelType: conversation.channelType || 'CHAT',
              contactEmail: email || undefined,
            } as unknown as Conversation;
          } catch {
            return null;
          }
        } catch {
          return null;
        }
      });

      // ALL in parallel - no waiting
      const parallelStart = Date.now();
      console.log(`⚡ Making ${allContacts.length} parallel API calls...`);
      const allResults = await Promise.all(conversationPromises);
      const parallelTime = Date.now() - parallelStart;
      console.log(`✅ All ${allContacts.length} calls completed in ${parallelTime}ms`);

      // Filter and sort
      const conversations = allResults
        .filter((c): c is Conversation => c !== null)
        .sort((a, b) => {
          const timeA = a.timestamp?.getTime?.() || 0;
          const timeB = b.timestamp?.getTime?.() || 0;
          return timeB - timeA;
        });

      const totalTime = Date.now() - startTime;
      console.log(`✅ COMPLETE: Loaded ${conversations.length} conversations in ${totalTime}ms total`);
      console.log(`   - Contact query: ${queryTime}ms (${Math.round(queryTime/totalTime*100)}%)`);
      console.log(`   - Parallel execution: ${parallelTime}ms (${Math.round(parallelTime/totalTime*100)}%)`);

      // NOTE: Phone-only conversations (anonymous visitors via SMS/voicemail) can only be discovered
      // through the onMessageSentToBusiness webhook event, which provides the anonymousVisitorId.
      // The Wix Inbox API does not provide a way to query all conversations or anonymous visitor IDs
      // without knowing them in advance. Implement webhook handler to capture these in real-time.

      // Final sort by timestamp (newest first)
      conversations.sort((a, b) => {
        const timeA = a.timestamp?.getTime?.() || 0;
        const timeB = b.timestamp?.getTime?.() || 0;
        return timeB - timeA;
      });

      console.log(`✅ FINAL: Loaded ${conversations.length} total conversations (from contact-based discovery only)`);

      if (typeof onProgressUpdate === 'function') {
        onProgressUpdate(conversations);
      }

      return conversations;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  }

  /**
   * Get a specific conversation with all its messages
   */
  static async getConversation(conversationId: string | number): Promise<Conversation | null> {
    try {
      const result = await callAdminApi('getConversation', 'inbox', { conversationId });
      return (result as unknown as Conversation) || null;
    } catch (error) {
      console.error('Error fetching conversation:', error);
      return null;
    }
  }

  /**
   * Get messages for a specific conversation
   *
   * CACHE-FIRST STRATEGY:
   * 1. Try Firestore cache (fast, cost-effective, 99% cost reduction)
   * 2. Fallback to direct Wix API if cache fails (slow, expensive)
   */
  static async getMessages(
    conversationId: string | number,
    _limit: number = 100,
    cursor?: string
  ): Promise<Message[]> {
    const startTime = Date.now();

    try {
      console.log(`📨 InboxService.getMessages: Attempting Firestore cache first for conversation ${conversationId}...`);

      // PRIMARY: Try Firestore cache
      const cacheService = FirestoreCacheService.getInstance();
      const cachedMessages = await cacheService.getCachedMessages(conversationId);

      if (cachedMessages && cachedMessages.length > 0) {
        const cacheTime = Date.now() - startTime;
        console.log(`✅ Loaded ${cachedMessages.length} messages from Firestore cache in ${cacheTime}ms`);
        return cachedMessages;
      }

      // FALLBACK: Cache empty or failed, use direct Wix API
      console.warn(`⚠️ Firestore cache empty for conversation ${conversationId}, falling back to direct Wix API (slow)`);
      const wixMessages = await this.getMessagesFromWixAPI(conversationId, _limit, cursor);
      
      // Try to enrich Wix messages with any available cached data
      // This preserves avatars and other metadata from previously cached messages
      try {
        const allCachedMessages = await cacheService.getCachedMessages(conversationId);
        if (allCachedMessages && allCachedMessages.length > 0) {
          console.log(`💾 Enriching ${wixMessages.length} Wix messages with ${allCachedMessages.length} cached messages for avatar preservation`);
          
          // Create a map of cached messages by ID for quick lookup
          const cachedById: Record<string, Message> = {};
          allCachedMessages.forEach(msg => {
            const msgId = (msg as Record<string, unknown>).id || (msg as Record<string, unknown>)._id;
            if (msgId) {
              cachedById[String(msgId)] = msg;
            }
          });
          
          // Merge cached data into Wix messages, preserving avatars
          const enrichedMessages = wixMessages.map(wixMsg => {
            const wixMsgId = (wixMsg as Record<string, unknown>).id || (wixMsg as Record<string, unknown>)._id;
            if (wixMsgId) {
              const cached = cachedById[String(wixMsgId)];
              if (cached) {
                // Merge cached data while keeping fresh Wix data as primary
                return {
                  ...cached, // Start with cached (has avatars and metadata)
                  ...wixMsg  // Override with fresh Wix data
                } as Message;
              }
            }
            return wixMsg;
          });
          
          console.log(`✅ Enriched Wix messages with cached metadata`);
          return enrichedMessages;
        }
      } catch (enrichError) {
        console.warn(`⚠️ Failed to enrich Wix messages with cache, returning Wix data as-is:`, enrichError);
      }
      
      return wixMessages;

    } catch (error) {
      console.error(`❌ Error in getMessages (cache attempt):`, error);

      // FALLBACK: On error, use direct Wix API
      console.warn(`⚠️ Firestore cache failed, falling back to direct Wix API`);
      return await this.getMessagesFromWixAPI(conversationId, _limit, cursor);
    }
  }

  /**
   * Load older messages from Wix API before a specific timestamp
   * Used for infinite scroll pagination - fetches messages older than the oldest cached message
   * 
   * @param conversationId - The conversation ID
   * @param beforeTimestamp - Fetch messages BEFORE this timestamp
   * @param limit - Maximum number of messages to fetch (default 30 for LRU cache)
   * @returns Array of older messages
   */
  static async getMessagesOlderThan(
    conversationId: string | number,
    beforeTimestamp: Date,
    limit: number = 30
  ): Promise<Message[]> {
    try {
      console.log(`⬆️ Loading messages before ${beforeTimestamp.toISOString()} for conversation ${conversationId}...`);

      // Fetch ALL messages from Wix API (bypassing cache since we need historical messages)
      const allMessages: Message[] = [];
      let cursor: string | undefined;
      let hasMore = true;
      let pageNum = 1;

      while (hasMore) {
        const result = await callAdminApi('getMessages', 'inbox', {
          conversationId,
          visibility: 'ALL',
          options: {
            paging: {
              limit: 100,
              ...(cursor && { cursor })
            }
          }
        });

        const pageMessages = (result?.messages as Message[]) || [];
        console.log(`📥 Page ${pageNum}: Retrieved ${pageMessages.length} messages from Wix API`);
        
        if (pageMessages.length === 0) {
          hasMore = false;
          break;
        }

        // IMPORTANT: Wix API doesn't return conversationId, so we need to add it manually
        const messagesWithConvId = pageMessages.map(msg => ({
          ...msg,
          conversationId: String(conversationId)
        }));

        allMessages.push(...messagesWithConvId);

        // Check if we have enough messages before the timestamp already
        const messagesBeforeTimestamp = allMessages.filter(msg => {
          const msgTimestamp = msg._createdDate instanceof Date ? msg._createdDate : new Date(msg._createdDate as string | number);
          return msgTimestamp < beforeTimestamp;
        });

        // If we have enough old messages, stop fetching
        if (messagesBeforeTimestamp.length >= limit) {
          console.log(`✅ Found enough old messages (${messagesBeforeTimestamp.length}), stopping pagination`);
          hasMore = false;
          break;
        }

        // Check for next page
        const pagingMetadata = (result as Record<string, unknown>)?.pagingMetadata as Record<string, unknown> | undefined;
        const nextCursor = (pagingMetadata?.cursors as Record<string, unknown>)?.next as string | undefined;

        if (!nextCursor) {
          console.log(`📥 No more pages available`);
          hasMore = false;
        } else {
          cursor = nextCursor;
          pageNum++;
        }
      }

      console.log(`📥 Total messages fetched from Wix API: ${allMessages.length}`);

      // DEBUG: Check what conversation IDs are in the fetched messages
      const conversationIds = new Set(allMessages.map(msg => msg.conversationId));
      console.log(`🔍 Conversation IDs in fetched messages:`, Array.from(conversationIds));
      console.log(`🔍 Expected conversationId: ${conversationId}`);
      console.log(`🔍 Timestamp to filter before: ${beforeTimestamp.toISOString()} (getTime: ${beforeTimestamp.getTime()})`);
      
      // Check current time to see if beforeTimestamp is in the past or future
      const now = new Date();
      console.log(`🔍 Current time: ${now.toISOString()} (getTime: ${now.getTime()})`);
      console.log(`🔍 beforeTimestamp is ${beforeTimestamp > now ? 'FUTURE' : 'PAST'} (diff: ${Math.abs(now.getTime() - beforeTimestamp.getTime())}ms)`);
      
      console.log(`🔍 Sample messages:`, allMessages.slice(0, 1).map(m => {
        console.log(`🔍 Full first message:`, JSON.stringify(m, null, 2));
        return m;
      }));

      // Log all timestamps to see the range and check for invalid ones
      let invalidTimestampCount = 0;
      const timestamps = allMessages.map(m => {
        try {
          const msgTimestamp = m._createdDate instanceof Date ? m._createdDate : new Date(m._createdDate as string | number);
          if (isNaN(msgTimestamp.getTime())) {
            invalidTimestampCount++;
            console.warn(`⚠️ INVALID timestamp found: ${String(m._createdDate)} for message ${m._id}`);
            return `INVALID: ${String(m._createdDate)}`;
          }
          return msgTimestamp.toISOString();
        } catch (e) {
          invalidTimestampCount++;
          console.warn(`⚠️ ERROR parsing timestamp: ${String(m._createdDate)} for message ${m._id}`, e);
          return `ERROR: ${String(m._createdDate)}`;
        }
      });
      console.log(`🔍 First message timestamp: ${timestamps[0]}`);
      console.log(`🔍 Last message timestamp: ${timestamps[timestamps.length - 1]}`);
      console.log(`🔍 Before timestamp (param): ${beforeTimestamp.toISOString()}`);
      console.log(`🔍 Before timestamp (getTime): ${beforeTimestamp.getTime()}`);
      
      // Show sample message timestamps for comparison
      if (allMessages.length > 0) {
        const firstMsg = allMessages[0];
        const firstTime = firstMsg._createdDate instanceof Date ? firstMsg._createdDate : new Date(firstMsg._createdDate as string | number);
        console.log(`🔍 Sample - First message getTime: ${firstTime.getTime()}, ISO: ${firstTime.toISOString()}`);
      }
      
      console.log(`⚠️ Invalid/Error timestamps found: ${invalidTimestampCount} out of ${allMessages.length}`);
      console.log(`🔍 All timestamps sorted:`, timestamps.slice().sort());

      // Filter to messages BEFORE the timestamp and sort by date (newest first)
      // Then take the first N messages, which will be the N newest messages before the timestamp
      const filteredMessages = allMessages
        .filter((msg, idx) => {
          try {
            const msgTimestamp = msg._createdDate instanceof Date ? msg._createdDate : new Date(msg._createdDate as string | number);
            // Check if timestamp is valid
            if (isNaN(msgTimestamp.getTime())) {
              console.warn(`⚠️ Invalid timestamp for message ${msg._id}: ${msg._createdDate}`);
              return false;
            }
            const isOlder = msgTimestamp < beforeTimestamp;
            // Log first 3 messages for debugging
            if (idx < 3) {
              console.log(`🔍 Message ${idx}: ${msgTimestamp.toISOString()} < ${beforeTimestamp.toISOString()} = ${isOlder}`);
            }
            return isOlder;
          } catch (_e) {
            console.warn(`⚠️ Error parsing timestamp for message ${msg._id}:`, _e);
            return false;
          }
        });

      console.log(`🔍 After filtering by timestamp: ${filteredMessages.length} messages (out of ${allMessages.length} total)`);
      console.log(`🔍 Messages removed by filter: ${allMessages.length - filteredMessages.length}`);

      const olderMessages = filteredMessages
        .sort((a, b) => {
          try {
            const aTime = a._createdDate instanceof Date ? a._createdDate : new Date(a._createdDate as string | number);
            const bTime = b._createdDate instanceof Date ? b._createdDate : new Date(b._createdDate as string | number);
            if (isNaN(aTime.getTime()) || isNaN(bTime.getTime())) return 0;
            return bTime.getTime() - aTime.getTime(); // Newest first (DESCENDING)
          } catch {
            return 0;
          }
        })
        .slice(0, limit) // Take the first N messages (which are now the N newest before timestamp)
        .reverse(); // Reverse to oldest-first for display

      console.log(`✅ Returning ${olderMessages.length} messages (after sorting and limiting to ${limit})`);
      return olderMessages;
    } catch (error) {
      console.error(`❌ Error loading older messages from Wix API:`, error);
      return [];
    }
  }

  /**
   * Get messages directly from Wix API, bypassing Firestore cache
   * Used for post-send operations where we need raw Wix messages before transformation
   * 
   * @param conversationId - The conversation ID
   * @param limit - Maximum number of messages to fetch
   * @param cursor - Pagination cursor
   * @returns Raw messages directly from Wix API (not cached or transformed)
   */
  static async getMessagesFromWixDirect(
    conversationId: string | number,
    limit: number = 100,
    cursor?: string
  ): Promise<Message[]> {
    console.log(`📨 InboxService.getMessagesFromWixDirect: Fetching directly from Wix (bypassing cache)`);
    return await this.getMessagesFromWixAPI(conversationId, limit, cursor);
  }

  /**
   * Get raw messages directly from Wix API WITHOUT normalization
   * Used internally for post-send matching where we need access to raw content structure
   * 
   * @param conversationId - The conversation ID
   * @param limit - Maximum number of messages to fetch
   * @param cursor - Pagination cursor
   * @returns Raw messages directly from Wix API (no normalization applied)
   */
  static async getMessagesFromWixRaw(
    conversationId: string | number,
    _limit: number = 100,
    cursor?: string
  ): Promise<Record<string, unknown>[]> {
    console.log(`📨 InboxService.getMessagesFromWixRaw: Fetching raw messages from Wix (no normalization)`);
    try {
      let allMessages: Record<string, unknown>[] = [];
      let currentCursor: string | undefined = cursor;
      let hasMore = true;
      let pageNum = 1;

      while (hasMore) {
        const result = await callAdminApi('getMessages', 'inbox', {
          conversationId,
          visibility: 'ALL',
          options: {
            paging: {
              limit: 100,
              ...(currentCursor && { cursor: currentCursor })
            }
          }
        });

        const messages = (result?.messages as Record<string, unknown>[]) || [];
        console.log(`📨 getMessagesFromWixRaw page ${pageNum}: ${messages.length} raw messages`);

        allMessages = allMessages.concat(messages);

        const pagingMetadata = (result as Record<string, unknown>)?.pagingMetadata as Record<string, unknown> | undefined;
        const nextCursor = (pagingMetadata?.cursors as Record<string, unknown>)?.next as string | undefined;

        if (!nextCursor || messages.length === 0) {
          hasMore = false;
        } else {
          currentCursor = nextCursor;
          pageNum++;
        }
      }

      console.log(`📨 getMessagesFromWixRaw FINAL: ${allMessages.length} total raw messages returned`);
      return allMessages;
    } catch (error) {
      console.error('Error fetching raw messages from Wix API:', error);
      return [];
    }
  }

  /**
   * FALLBACK METHOD: Get messages directly from Wix API
   * Only used when Firestore cache is empty or fails
   */
  private static async getMessagesFromWixAPI(
    conversationId: string | number,
    _limit: number = 100,
    cursor?: string
  ): Promise<Message[]> {
    try {
      let allMessages: Message[] = [];
      let currentCursor: string | undefined = cursor;
      let hasMore = true;
      let pageNum = 1;

      console.log(`📨 getMessagesFromWixAPI called with conversationId: ${conversationId}`);

      // Fetch all pages of messages using cursor-based pagination
      while (hasMore) {
        const result = await callAdminApi('getMessages', 'inbox', {
          conversationId,
          visibility: 'ALL', // Fetch all messages including those with visibility="BUSINESS"
          options: {
            paging: {
              limit: 100,
              ...(currentCursor && { cursor: currentCursor })
            }
          }
        });

        // Get messages from this page
        let messages = (result?.messages as unknown as Array<{ content?: { contentType?: string } }>) || [];

        console.log(`📨 getMessagesFromWixAPI page ${pageNum}: ${messages.length} raw messages`);
        
        // Log message types for debugging
        if (messages.length > 0) {
          const messageTypes = messages.map(m => {
            const contentType = (m as Record<string, unknown>).content && typeof (m as Record<string, unknown>).content === 'object' 
              ? ((m as Record<string, unknown>).content as Record<string, unknown>).contentType 
              : 'UNKNOWN';
            return contentType;
          });
          console.log(`📨 Message types on page ${pageNum}: ${messageTypes.join(', ')}`);
          
          // Log any FORM messages found and log full structure of first few messages
          const formMessages = messages.filter(m => 
            (m as Record<string, unknown>).content && 
            typeof (m as Record<string, unknown>).content === 'object' &&
            ((m as Record<string, unknown>).content as Record<string, unknown>).contentType === 'FORM'
          );
          if (formMessages.length > 0) {
            console.log(`📋 FOUND ${formMessages.length} FORM messages on this page!`);
            formMessages.forEach((msg, idx) => {
              console.log(`📋 FORM Message ${idx}:`, JSON.stringify(msg, null, 2));
            });
          }


        }

        // Don't filter - return all messages
        // The filtering happens at conversation list level, not message level

        console.log(`📨 getMessagesFromWixAPI page ${pageNum}: ${messages.length} messages, total so far: ${allMessages.length + messages.length}`);

        allMessages = allMessages.concat(messages as unknown as Message[]);

        // Check for next page
        const pagingMetadata = (result as Record<string, unknown>)?.pagingMetadata as Record<string, unknown> | undefined;
        const nextCursor = (pagingMetadata?.cursors as Record<string, unknown>)?.next as string | undefined;

        console.log(`📨 getMessagesFromWixAPI page ${pageNum}: nextCursor=${!!nextCursor}`);

        if (!nextCursor || messages.length === 0) {
          hasMore = false;
        } else {
          currentCursor = nextCursor;
          pageNum++;
        }
      }

      console.log(`📨 getMessagesFromWixAPI FINAL: ${allMessages.length} total messages returned`);

      // Try to fetch form submissions for this conversation and merge them
      try {
        const formSubmissions = await this.getFormSubmissionsForConversation(conversationId);
        if (formSubmissions && formSubmissions.length > 0) {
          console.log(`📋 Found ${formSubmissions.length} form submissions to merge with messages`);
          allMessages = allMessages.concat(formSubmissions);
          // Sort by timestamp descending
          allMessages.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
        }
      } catch (formError) {
        console.warn(`⚠️ Could not fetch form submissions:`, formError);
        // Continue without form submissions - don't fail the whole operation
      }

      // Transform messages to ensure all have text field
      return allMessages.map(msg => this.normalizeMessage(msg as Record<string, unknown>));
    } catch (error) {
      console.error('Error fetching messages from Wix API:', error);
      return [];
    }
  }

  /**
   * Normalize message to ensure it always has a displayable text field
   * Handles form submissions, system messages, and regular text messages
   */
  private static normalizeMessage(msg: Record<string, unknown>): Message {
    // Extract message text from various possible locations
    let text = '';
    
    if (msg.text && typeof msg.text === 'string') {
      text = msg.text;
    } else if ((msg.content as Record<string, unknown>)?.text && typeof (msg.content as Record<string, unknown>).text === 'string') {
      text = (msg.content as Record<string, unknown>).text as string;
    } else if ((msg.content as Record<string, unknown>)?.contentType === 'FORM') {
      text = '📋 Form submission received';
    } else if (msg.actionType && typeof msg.actionType === 'string') {
      const action = msg.actionType;
      if (action === 'EMAIL_RECEIVED') text = '📧 Email received';
      else if (action === 'EMAIL_OPENED') text = '📖 Email opened';
      else if (action === 'EMAIL_CLICKED') text = '🔗 Email link clicked';
      else if (action === 'CONTACT_BECAME_MEMBER') text = '👥 Contact became member';
      else if (action === 'CUSTOM_ACTION') text = '⚙️ Custom action';
      else text = `⚙️ ${action}`;
    } else {
      text = '📦 Message';
    }

    return {
      ...msg,
      text: text
    } as Message;
  }

  /**
   * Format staff message for Wix dashboard display
   * Only shows staff FIRST NAME in Wix, NOT the avatar URL or last name
   * Format: "FirstName: message text"
   * Wix will show: Business → Participant: "Sarah: I'm on my way!"
   * (clean, first name only, no avatar URL visible)
   */
  static formatStaffMessageForWix(messageText: string, staffName: string): string {
    // Extract first name only for Wix display
    const firstName = staffName.trim().split(/\s+/)[0];
    const formatted = `${firstName}: ${messageText}`;
    console.log(`🎯 formatStaffMessageForWix: "${staffName}" → "${firstName}" (first name only for Wix)`);
    return formatted;
  }

  /**
   * Parse staff info from message format
   * Formats supported:
   * 1. "StaffName|avatarUrl: message text" (new format with embedded avatar)
   * 2. "StaffName: message text" (fallback without avatar)
   * Returns the cleaned message text, extracted staff name, and avatar URL
   * If no match, returns original text (customer message or system message)
   */
  static parseStaffMessageFromWix(messageText: string): { staffName?: string; avatarUrl?: string; cleanText: string } {
    console.log(`🔍 parseStaffMessageFromWix FULL MESSAGE TEXT: "${messageText}"`);
    console.log(`🔍 Message length: ${messageText.length}`);
    console.log(`🔍 Contains pipe |? ${messageText.includes('|')}`);
    console.log(`🔍 Contains https? ${messageText.includes('https')}`);
    
    // Try to extract "StaffName|avatarUrl: message" format first (new format with avatar)
    // Match pattern: Name|URL: Message where URL can contain colons
    // Use greedy .+ for the URL so it captures everything up to the LAST ": " before the message
    const regexWithAvatar = /^([A-Za-z\s]+)\|(.+): (.+)$/;
    let match = messageText.match(regexWithAvatar);
    
    console.log(`🔍 Trying avatar regex: ${regexWithAvatar}`);
    console.log(`🔍 Avatar regex match result: ${match ? 'MATCHED' : 'NO MATCH'}`);
    if (match && match.length >= 4) {
      console.log(`🔍 Avatar regex matched! Groups: [1]="${match[1]}", [2] length=${match[2].length}, [2] ends with="${match[2].substring(match[2].length - 30)}", [3]="${match[3].substring(0, 30)}..."`);
      
      const staffName = match[1].trim();
      const avatarUrl = match[2].trim();
      const cleanText = match[3];
      
      // Validate it's a staff name (not just any text)
      if (/^[A-Za-z\s]+$/.test(staffName) && staffName.length > 0 && avatarUrl.length > 0) {
        console.log(`✅ parseStaffMessageFromWix: extracted staffName="${staffName}", avatarUrl="${avatarUrl}", cleanText="${cleanText}"`);
        return {
          staffName,
          avatarUrl,
          cleanText
        };
      }
    } else {
      console.log(`🔍 Avatar regex did NOT match`);
    }
    
    // Try to extract "StaffName: message" format (fallback, no avatar)
    const regex = /^([A-Za-z\s]+):\s+(.*)$/m;
    match = messageText.trim().match(regex);
    
    if (match) {
      const potentialStaffName = match[1].trim();
      const cleanText = match[2].trim();
      
      // Only treat as staff message if name looks like a person
      if (/^[A-Za-z\s]+$/.test(potentialStaffName) && potentialStaffName.length > 0) {
        console.log(`✅ parseStaffMessageFromWix: extracted staffName="${potentialStaffName}", cleanText="${cleanText}" (no avatar)`);
        return {
          staffName: potentialStaffName,
          cleanText
        };
      }
    }
    
    return { cleanText: messageText };
  }

  /**
   * Send a message to a conversation
   * Maps UI channels to Wix API channel format
   * Staff info is stored locally on the app side for display, not embedded in Wix message
   *
   * @param conversationId - The conversation ID (passed via URL, not by contactId)
   * @param text - The message text to send
   * @param channel - The channel: 'chat' (CHAT), 'email' (EMAIL), 'sms' (SMS)
   * @param sendAsContactId - Optional contact ID to send the message as (for staff members)
   * @param staffName - Optional staff member name (for app-side display only)
   * @param staffId - Optional staff member ID (for app-side display only)
   * @param contactId - Optional contact ID (for app-side display only)
   * @param avatarUrl - Optional staff member avatar URL (for app-side display only)
   * @param contactName - Optional contact name (for draft metadata)
   * @param contactAvatarUrl - Optional contact avatar (for draft metadata)
   */
  static async sendMessage(
    conversationId: string | number,
    text: string,
    channel: 'chat' | 'email' | 'sms' = 'chat',
    sendAsContactId?: string,
    staffName?: string,
    staffId?: string,
    contactId?: string,
    avatarUrl?: string,
    contactName?: string,
    contactAvatarUrl?: string
  ): Promise<Message | null> {
    try {
      if (!conversationId) throw new Error('Conversation ID is required');
      if (!text) throw new Error('Message text is required');
      
      // Wix will show: Business → Participant: "Sarah|avatarUrl: I'm on my way!"
      let messageToSend = text;
      console.log(`🔍 [SEND DEBUG] Raw inputs: staffName="${staffName}", staffId="${staffId}", contactId="${contactId}", avatarUrl="${avatarUrl}"`);
      console.log(`🔍 [SEND DEBUG] Conditions: !!staffName=${!!staffName}, !!staffId=${!!staffId}, !!contactId=${!!contactId}, !!avatarUrl=${!!avatarUrl}`);
      
      if (staffName && staffId && contactId) {
        messageToSend = this.formatStaffMessageForWix(text, staffName);
        console.log(`✅ Formatted staff message for Wix dashboard`);
        console.log(`📨 Message to Wix: "${messageToSend}"`);
      } else {
        console.log(`⚠️ NOT formatting as staff message: staffName=${!!staffName}, staffId=${!!staffId}, contactId=${!!contactId}`);
      }
      
      console.log(`🔍 [SEND DEBUG] messageToSend final value: "${messageToSend}"`);
      
      // Map UI channel names to Wix API channel names
      const channelMap: Record<'chat' | 'email' | 'sms', 'CHAT' | 'EMAIL' | 'SMS'> = {
        'chat': 'CHAT',
        'email': 'EMAIL',
        'sms': 'SMS'
      };
      const wixChannel = channelMap[channel] || 'CHAT';
      
      console.log(`📤 Frontend: sendMessage called with:`, {
        conversationId: conversationId,
        conversationIdType: typeof conversationId,
        text: text.substring(0, 50),
        hasStaffInfo: !!(staffName && staffId && contactId),
        channel,
        wixChannel,
        sendAsContactId
      });
      
      
      // Build message object using BASIC message type per Wix documentation
      // Use messageToSend which includes embedded staff info if applicable
      const message: Record<string, unknown> & { metadata?: Record<string, unknown> } = {
        content: {
          basic: {
            items: [
              {
                text: messageToSend
              }
            ]
          }
        },
        visibility: 'ALL',
        direction: 'BUSINESS_TO_PARTICIPANT',
        sourceChannelId: wixChannel
      };
      
      // Embed staff metadata if available (e.g., for "Helix" workaround messages)
      // This metadata will be stored by Wix and returned when we fetch messages
      if (staffName && staffId && contactId) {
        // Format: firstName only (e.g., "Helix" is stored as contact but actual staff is embedded)
        message.metadata = {
          extractedStaffInfo: {
            staffName: staffName,
            staffId: staffId,
            contactId: contactId,
            avatarUrl: avatarUrl
          }
        };
        console.log(`📦 Embedded metadata in message for staff enrichment:`, message.metadata);
      }
      
      // Build options for sendMessage - sendAs goes here (3rd parameter to Wix SDK)
      const options: Record<string, unknown> = {};
      if (sendAsContactId) {
        // sendAs tells Wix to send this message as a specific contact
        options.sendAs = sendAsContactId;
      }
      
      console.log(`📤 Sending ${wixChannel} message to conversation ${conversationId}`);
      console.log(`📤 Message text being sent: "${messageToSend.substring(0, 100)}..."`);
      console.log(`📤 Message object:`, JSON.stringify(message, null, 2));
      if (Object.keys(options).length > 0) {
        console.log(`📤 Options object:`, JSON.stringify(options, null, 2));
      }
      
      // Build the API payload with participant metadata for draft writing
      const payload: Record<string, unknown> = {
        conversationId,
        message,
        // Pass staff metadata so backend can write conversationDraft with sender info
        staffName: staffName,
        staffId: staffId,
        contactId: contactId,
        staffAvatar: avatarUrl,
        // Pass contact metadata so backend can write conversationDraft with participant info
        // This is CRITICAL - webhook doesn't have this data, so draft provides it
        contactName: contactName,
        avatarUrl: contactAvatarUrl
      };

      // Only add options if there are any
      if (Object.keys(options).length > 0) {
        payload.options = options;
      }

      const result = await callAdminApi('sendMessage', 'inbox', payload);

      console.log(`✅ Message sent successfully to Wix`);

      // Webhook should handle caching new messages to Firestore with staff metadata
      // No need to manually cache here - the webhook will add the metadata when it fires

      return (result as unknown as Message) || null;
    } catch (error) {
      console.error('❌ Error sending message:', error);
      return null;
    }
  }

  /**
   * Start a new conversation with a contact
   */
  static async startConversation(
    contactId?: string,
    participantIds: string[] = [],
    firstMessage: string = ''
  ): Promise<Conversation | null> {
    try {
      // Build participants list - business logic
      const participants = contactId ? [contactId, ...participantIds] : participantIds;
      if (participants.length === 0) {
        throw new Error('At least one participant is required');
      }

      const result = await callAdminApi('startConversation', 'inbox', {
        participantIds: participants,
        firstMessage: firstMessage || '',
      }) as {
        conversation?: {
          id?: string;
          _id?: string;
          channelType?: string;
          participants?: Array<Record<string, unknown>>;
        };
        id?: string;
        _id?: string;
        channelType?: string;
      };

      // Extract conversation from response
      const conversation = result?.conversation || result;
      const conversationId = conversation?.id || conversation?._id;

      console.log(`📭 startConversation response:`, {
        result,
        conversation,
        conversationId,
        hasId: !!conversationId
      });

      if (!conversationId) {
        console.error('startConversation: No conversation ID in response:', result);
        return null;
      }

      // Participant info will be stored by webhook when first message is sent
      // No need to cache here - webhook handles it

      // Return properly mapped conversation object
      return {
        id: conversationId,
        name: '',
        lastMessage: '',
        timestamp: new Date(),
        messages: [],
        avatarUrl: undefined,
        unread: false,
        unreadCount: 0,
        starred: false,
        archived: false,
        priority: 'medium',
        channelType: conversation.channelType || 'CHAT',
      } as Conversation;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }

  /**
   * Mark a conversation as read
   */
  static async markAsRead(conversationId: string | number): Promise<boolean> {
    try {
      if (!conversationId) throw new Error('Conversation ID is required');
      
      await callAdminApi('markAsRead', 'inbox', { conversationId });
      return true;
    } catch (error) {
      console.error('Error marking conversation as read:', error);
      return false;
    }
  }

  /**
   * Get form submissions for a conversation and convert to Message format
   */
  private static async getFormSubmissionsForConversation(conversationId: string | number): Promise<Message[]> {
    try {
      // Get the conversation details to find the contact ID
      const conversation = await callAdminApi('getConversation', 'inbox', { conversationId });
      
      if (!conversation) {
        console.warn(`⚠️ Could not find conversation ${conversationId}`);
        return [];
      }

      // Extract contact ID from conversation participants
      const conversationData = conversation as Record<string, unknown>;
      const participants = conversationData.participants as Array<Record<string, unknown>> | undefined;
      const contact = participants?.find((p: Record<string, unknown>) => p.participantType === 'CONTACT');
      const contactId = contact?.id ? String(contact.id) : undefined;
      
      if (!contactId) {
        console.warn(`⚠️ Could not find contact ID in conversation ${conversationId}`);
        return [];
      }

      console.log(`📋 Fetching form submissions for contact ${contactId} in conversation ${conversationId}`);

      // Fetch form submissions for this contact
      const formResult = await callAdminApi('getContactFormSubmissions', 'forms', { contactId });
      
      if (!formResult || !Array.isArray(formResult.items)) {
        console.log(`📋 No form submissions found for contact ${contactId}`);
        return [];
      }

      // Convert form submissions to Message format
      const formMessages: Message[] = (formResult.items as Array<Record<string, unknown>>).map(submission => {
        const formId = submission.formId || 'unknown-form';
        const formName = submission.formName || 'Form Submission';
        const submittedDate = submission.createdDate ? new Date(submission.createdDate as string | number) : new Date();
        
        // Extract form field values
        const fieldValues = submission.fieldValues || [];
        let fieldText = '';
        if (Array.isArray(fieldValues) && fieldValues.length > 0) {
          fieldText = fieldValues
            .map((field: Record<string, unknown>) => `${field.label || field.name}: ${field.value}`)
            .join('\n');
        }

        return {
          id: submission._id || `form-${formId}-${submittedDate.getTime()}`,
          text: `📋 ${formName} submitted`, // Will be overridden by normalizeMessage
          timestamp: submittedDate,
          senderId: contactId,
          senderName: (submission.submitterEmail ? `${submission.submitterEmail}` : 'Contact'),
          sender: {
            contactId: contactId,
            name: submission.submitterEmail || 'Contact'
          },
          channel: 'chat',
          content: {
            contentType: 'FORM',
            text: `${formName}${fieldText ? `\n\n${fieldText}` : ''}`,
            previewText: formName
          },
          visibility: 'PARTICIPANT',
          _createdDate: submittedDate.toISOString(),
          direction: 'PARTICIPANT_TO_BUSINESS'
        } as unknown as Message;
      });

      console.log(`✅ Converted ${formMessages.length} form submissions to messages`);
      return formMessages;
    } catch (error) {
      console.error(`❌ Error fetching form submissions:`, error);
      return [];
    }
  }

  /**
   * Archive a conversation
   */
  static async archiveConversation(conversationId: string | number): Promise<boolean> {
    try {
      if (!conversationId) throw new Error('Conversation ID is required');

      await callAdminApi('archiveConversation', 'inbox', { conversationId });
      return true;
    } catch (error) {
      console.error('Error archiving conversation:', error);
      return false;
    }
  }

  /**
   * Subscribe to real-time message updates from Firestore cache
   * Automatically updates UI when new messages arrive
   *
   * @param conversationId - The conversation ID to listen to
   * @param callback - Function to call with updated messages
   * @returns Unsubscribe function to stop listening
   */
  static async subscribeToMessages(
    conversationId: string | number,
    callback: (messages: Message[]) => void
  ): Promise<() => void> {
    try {
      const cacheService = FirestoreCacheService.getInstance();
      return cacheService.subscribeToMessages(conversationId, callback);
    } catch (error) {
      console.error('❌ Error subscribing to messages:', error);
      return () => {};
    }
  }

  /**
   * Unsubscribe from real-time message updates
   *
   * @param conversationId - The conversation ID to stop listening to
   */
  static async unsubscribeFromMessages(conversationId: string | number): Promise<void> {
    try {
      const cacheService = FirestoreCacheService.getInstance();
      cacheService.unsubscribeFromMessages(conversationId);
    } catch (error) {
      console.error('❌ Error unsubscribing from messages:', error);
    }
  }

  /**
   * Bulk cleanup: Delete all cached messages except the newest 30 for a conversation
   * Use this when a conversation has too many cached messages
   *
   * @param conversationId - The conversation ID to clean up
   */
  static async cleanupConversationCache(conversationId: string | number): Promise<void> {
    try {
      const cacheService = FirestoreCacheService.getInstance();
      await cacheService.bulkCleanupConversation(conversationId);
    } catch (error) {
      console.error('❌ Error cleaning up conversation cache:', error);
      throw error;
    }
  }

  /**
   * Initialize cache: Sync the newest 30 messages from Wix to Firestore
   * Call this on first load to ensure the cache is up-to-date
   *
   * @param conversationId - The conversation ID to initialize
   * @param wixMessages - The messages from Wix API (will take newest 30)
   */
  static async initializeConversationCache(conversationId: string | number, wixMessages: Message[]): Promise<void> {
    try {
      const cacheService = FirestoreCacheService.getInstance();
      await cacheService.initializeConversationCache(conversationId, wixMessages);
    } catch (error) {
      console.error('❌ Error initializing conversation cache:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time conversation updates from Firestore cache
   * Automatically updates UI when conversations change (new messages, etc)
   *
   * @param callback - Function to call with updated conversations
   * @returns Unsubscribe function to stop listening
   */
  static subscribeToConversations(
    callback: (conversations: Conversation[]) => void,
    onError?: (error: unknown) => void
  ): () => void {
    try {
      const cacheService = ConversationCacheService.getInstance();
      return cacheService.subscribeToConversations(callback, onError);
    } catch (error) {
      console.error('❌ Error subscribing to conversations:', error);
      return () => {};
    }
  }

  /**
   * Subscribe to conversationDrafts for instant optimistic UI updates
   * Drafts are written immediately when sending messages, before webhook fires
   * UI shows drafts until canonical conversation is created by webhook
   *
   * @param callback - Function to call with updated drafts
   * @returns Unsubscribe function to stop listening
   */
  static subscribeToConversationDrafts(
    callback: (drafts: Conversation[]) => void
  ): () => void {
    try {
      const cacheService = ConversationCacheService.getInstance();
      return cacheService.subscribeToConversationDrafts(callback);
    } catch (error) {
      console.error('❌ Error subscribing to conversationDrafts:', error);
      return () => {};
    }
  }

  /**
   * Unsubscribe from real-time conversation updates
   */
  static unsubscribeFromConversations(): void {
    try {
      const cacheService = ConversationCacheService.getInstance();
      cacheService.unsubscribeFromConversations();
    } catch (error) {
      console.error('❌ Error unsubscribing from conversations:', error);
    }
  }

  /**
   * Register cleanup handler for session cache on app close
   * This unsubscribes from real-time listeners and clears session memory
   */
  static registerCleanupOnClose(): void {
    // Handle page unload - cleanup subscriptions and clear session cache
    const cleanup = async () => {
      console.log('✅ InboxService cleanup: clearing resources on app close');
      // Cleanup Firestore listeners
      this.unsubscribeFromConversations();

      // Cleanup message listeners
      try {
        const cacheService = FirestoreCacheService.getInstance();
        cacheService.unsubscribeAll();
      } catch (error) {
        console.error('Error cleaning up message subscriptions:', error);
      }
    };

    // Register cleanup on page unload/close
    window.addEventListener('beforeunload', cleanup);
  }

  /**
   * Load next page of messages using cursor-based pagination
   * Skips Firestore cache and goes directly to Wix API
   * Perfect for scrolling to load more historical messages
   * 
   * @param conversationId - The conversation ID
   * @param limit - Messages to fetch (default 30)
   * @param cursor - Pagination cursor from previous page (undefined for first page)
   * @returns Array of messages and next cursor for continued pagination
   */
  /**
   * Load next page of older messages for pagination
   * On first call (cursor undefined), skips the 30 newest (already in Firestore cache)
   * and loads the NEXT 30 from Wix
   * 
   * @param conversationId - The conversation ID
   * @param limit - Messages to fetch (default 30)
   * @param cursor - Pagination cursor from previous page (undefined for first page after cache)
   * @param skipFirstPage - If true on first call, fetches 60 messages and returns last 30 (skips the cached 30)
   * @returns Array of messages and next cursor for continued pagination
   */
  static async getMessagesNextPage(
    conversationId: string | number,
    limit: number = 30,
    cursor?: string,
    skipFirstPage: boolean = false
  ): Promise<{ messages: Message[]; nextCursor?: string }> {
    try {
      const isFirstPage = !cursor;

      console.log(`⬆️ getMessagesNextPage called: isFirstPage=${isFirstPage}, skipFirstPage=${skipFirstPage}, cursor="${cursor}"`);

      // If skipFirstPage is true and no cursor, we need to fetch page 1 first to get its cursor,
      // then use that cursor to fetch page 2 (the actual older messages past cache)
      if (isFirstPage && skipFirstPage) {
        console.log(`⬆️ SKIP FIRST PAGE: Fetching page 1 to get cursor, then fetching page 2`);

        // Fetch first page (these are the cached messages we want to skip)
        const firstPageResult = await callAdminApi('getMessages', 'inbox', {
          conversationId,
          visibility: 'ALL',
          options: {
            paging: { limit }
          }
        });

        const firstPageMessages = (firstPageResult?.messages as Message[]) || [];
        console.log(`⬆️ Page 1: Got ${firstPageMessages.length} messages (cached, skipping)`);

        // Get cursor for next page
        const firstPageMeta = (firstPageResult as Record<string, unknown>)?.pagingMetadata as Record<string, unknown> | undefined;
        const page2Cursor = (firstPageMeta?.cursors as Record<string, unknown>)?.next as string | undefined;

        if (!page2Cursor) {
          // No more pages - conversation has <= limit messages total (all in cache)
          console.log(`⬆️ No page 2 cursor - conversation has ${firstPageMessages.length} messages total (all cached)`);
          return { messages: [], nextCursor: undefined };
        }

        console.log(`⬆️ Got page 2 cursor, fetching older messages...`);

        // Now fetch page 2 using the cursor (these are the older messages we want)
        const secondPageResult = await callAdminApi('getMessages', 'inbox', {
          conversationId,
          visibility: 'ALL',
          options: {
            paging: { limit, cursor: page2Cursor }
          }
        });

        const pageMessages = (secondPageResult?.messages as Message[]) || [];
        console.log(`✅ Page 2: Got ${pageMessages.length} older messages`);

        // Get cursor for page 3
        const secondPageMeta = (secondPageResult as Record<string, unknown>)?.pagingMetadata as Record<string, unknown> | undefined;
        const nextCursor = (secondPageMeta?.cursors as Record<string, unknown>)?.next as string | undefined;

        if (nextCursor) {
          console.log(`⬆️ Page 3 cursor available for continued pagination`);
        } else {
          console.log(`⬆️ No more pages after page 2`);
        }

        return { messages: pageMessages, nextCursor };
      }

      // Normal pagination (not skipping first page)
      console.log(`⬆️ Loading page of messages for conversation ${conversationId} (limit: ${limit}, cursor: ${cursor ? 'YES' : 'INITIAL'})`);

      const result = await callAdminApi('getMessages', 'inbox', {
        conversationId,
        visibility: 'ALL',
        options: {
          paging: {
            limit,
            ...(cursor && { cursor })
          }
        }
      });

      const pageMessages = (result?.messages as Message[]) || [];
      console.log(`✅ Retrieved ${pageMessages.length} messages from Wix API`);

      // Get next cursor for pagination
      const pagingMetadata = (result as Record<string, unknown>)?.pagingMetadata as Record<string, unknown> | undefined;
      const nextCursor = (pagingMetadata?.cursors as Record<string, unknown>)?.next as string | undefined;

      if (nextCursor) {
        console.log(`⬆️ Next cursor available for continued pagination`);
      } else {
        console.log(`⬆️ No more pages available (reached end of conversation history)`);
      }

      return {
        messages: pageMessages,
        nextCursor
      };
    } catch (error) {
      console.error(`❌ Error loading next page:`, error);
      return {
        messages: [],
        nextCursor: undefined
      };
    }
  }
}

